import React, {
    useEffect,
    useState,
  } from "react";
  
  import {
    collection,
    query,
    where,
    getDocs,
    doc,
    runTransaction,
    serverTimestamp,
  } from "firebase/firestore";
  
  import { db } from "../firebase";
  
  import "./WeeklyDepositBonusUSDT.css";
  
  
  /* =========================================================
     COLLECTIONS
  ========================================================= */
  
  const USDT_COLLECTION =
    "usdt_transactions";
  
  const VISITOR_COLLECTION =
    "visterdeposte";
  
  const USERS_COLLECTION =
    "users";
  
  
  /* =========================================================
     CURRENCY
  ========================================================= */
  
  const USDT_CURRENCY =
    "USDT";
  
  /*
    IMPORTANT:
    Correct Firebase field is:
  
    usdtBalance
  
    NOT:
  
    USDTBalance
  */
  const USDT_BALANCE_FIELD =
    "usdtBalance";
  
  
  /* =========================================================
     WEEKLY BONUS SETTINGS
  ========================================================= */
  
  const WEEKLY_PERCENT =
    5;
  
  
  /* =========================================================
     FORMAT MONEY
  ========================================================= */
  
  const formatMoney = (
    amount,
    currency = USDT_CURRENCY
  ) => {
  
    const numericAmount =
      Number(amount || 0);
  
    return `${numericAmount.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      }
    )} ${currency}`;
  
  };
  
  
  /* =========================================================
     NORMALIZE USERNAME
  ========================================================= */
  
  const normalizeUsername = (
    username
  ) => {
  
    return String(
      username || ""
    )
      .trim()
      .toLowerCase();
  
  };
  
  
  /* =========================================================
     NORMALIZE STATUS
  ========================================================= */
  
  const normalizeStatus = (
    status
  ) => {
  
    return String(
      status || ""
    )
      .trim()
      .toLowerCase();
  
  };
  
  
  /* =========================================================
     TIMESTAMP
  ========================================================= */
  
  const getTimestampSeconds = (
    item
  ) => {
  
    const value =
      item?.createdAt ||
      item?.timestamp ||
      item?.paidAt ||
      item?.updatedAt;
  
  
    if (!value) {
  
      return 0;
  
    }
  
  
    /* Firestore Timestamp */
  
    if (
      typeof value?.seconds ===
      "number"
    ) {
  
      return Number(
        value.seconds
      );
  
    }
  
  
    /* Firestore Timestamp with toDate() */
  
    if (
      typeof value?.toDate ===
      "function"
    ) {
  
      const date =
        value.toDate();
  
  
      if (
        date instanceof Date &&
        !Number.isNaN(
          date.getTime()
        )
      ) {
  
        return Math.floor(
          date.getTime() / 1000
        );
  
      }
  
    }
  
  
    /* Numeric timestamp */
  
    if (
      typeof value ===
      "number"
    ) {
  
      return value >
        100000000000
  
        ? Math.floor(
            value / 1000
          )
  
        : Math.floor(
            value
          );
  
    }
  
  
    /* String date */
  
    const parsed =
      new Date(
        value
      ).getTime();
  
  
    if (
      Number.isNaN(
        parsed
      )
    ) {
  
      return 0;
  
    }
  
  
    return Math.floor(
      parsed / 1000
    );
  
  };
  
  
  /* =========================================================
     WEDNESDAY CHECK
  ========================================================= */
  
  const isWednesday = (
    timestampSeconds
  ) => {
  
    if (
      !timestampSeconds
    ) {
  
      return false;
  
    }
  
  
    const date =
      new Date(
        Number(
          timestampSeconds
        ) * 1000
      );
  
  
    return (
      date.getDay() === 3
    );
  
  };
  
  
  /* =========================================================
     GET TRANSACTION CURRENCY
  ========================================================= */
  
  const getTransactionCurrency = (
    item
  ) => {
  
    /*
      Anything coming from
      usdt_transactions is USDT.
    */
  
    if (
      item?.source ===
      USDT_COLLECTION
    ) {
  
      return USDT_CURRENCY;
  
    }
  
  
    /*
      Visitor deposits can store
      currency/sourceCurrency.
    */
  
    if (
      item?.currency
    ) {
  
      return String(
        item.currency
      )
        .trim()
        .toUpperCase();
  
    }
  
  
    if (
      item?.sourceCurrency
    ) {
  
      return String(
        item.sourceCurrency
      )
        .trim()
        .toUpperCase();
  
    }
  
  
    return "";
  
  };
  
  
  /* =========================================================
     CHECK USDT TRANSACTION
  ========================================================= */
  
  const isUSDTTransaction = (
    item
  ) => {
  
    const currency =
      getTransactionCurrency(
        item
      );
  
  
    return (
      currency ===
      USDT_CURRENCY
    );
  
  };
  
  
  /* =========================================================
     NORMALIZE USDT DEPOSIT
  ========================================================= */
  
  const normalizeUsdtDeposit = (
    document
  ) => {
  
    const data =
      document.data();
  
  
    const amount =
      Number(
        data.amount || 0
      );
  
  
    return {
  
      id:
        document.id,
  
      ...data,
  
      source:
        USDT_COLLECTION,
  
      sourceCurrency:
        USDT_CURRENCY,
  
      originalAmount:
        amount,
  
    };
  
  };
  
  
  /* =========================================================
     NORMALIZE VISITOR USDT DEPOSIT
  ========================================================= */
  
  const normalizeVisitorDeposit = (
    document
  ) => {
  
    const data =
      document.data();
  
  
    const amount =
      Number(
        data.amount || 0
      );
  
  
    const sourceCurrency =
      getTransactionCurrency(
        data
      );
  
  
    return {
  
      id:
        document.id,
  
      ...data,
  
      source:
        VISITOR_COLLECTION,
  
      sourceCurrency,
  
      originalAmount:
        amount,
  
    };
  
  };
  
  
  /* =========================================================
     FIND USER
  ========================================================= */
  
  const findUserByUsername = async (
    username
  ) => {
  
    if (!username) {
  
      return null;
  
    }
  
  
    const usersRef =
      collection(
        db,
        USERS_COLLECTION
      );
  
  
    /* =======================================================
       EXACT USERNAME SEARCH
    ======================================================= */
  
    try {
  
      const exactQuery =
        query(
  
          usersRef,
  
          where(
            "username",
            "==",
            username
          )
  
        );
  
  
      const snapshot =
        await getDocs(
          exactQuery
        );
  
  
      if (
        !snapshot.empty
      ) {
  
        return snapshot.docs[0];
  
      }
  
    }
  
    catch (error) {
  
      console.warn(
        "Exact username lookup failed:",
        error
      );
  
    }
  
  
    /* =======================================================
       CASE INSENSITIVE FALLBACK
    ======================================================= */
  
    const allUsers =
      await getDocs(
        usersRef
      );
  
  
    const normalized =
      normalizeUsername(
        username
      );
  
  
    return (
  
      allUsers.docs.find(
        userDoc =>
  
          normalizeUsername(
            userDoc.data()?.username
          ) ===
          normalized
  
      ) || null
  
    );
  
  };
  
  
  /* =========================================================
     GET WEDNESDAY KEY
  ========================================================= */
  
  const getWednesdayKey = (
    timestampSeconds
  ) => {
  
    const date =
      new Date(
        Number(
          timestampSeconds
        ) * 1000
      );
  
  
    const year =
      date.getFullYear();
  
  
    const month =
      String(
        date.getMonth() + 1
      ).padStart(
        2,
        "0"
      );
  
  
    const day =
      String(
        date.getDate()
      ).padStart(
        2,
        "0"
      );
  
  
    return `${year}-${month}-${day}`;
  
  };
  
  
  /* =========================================================
     GET WEDNESDAY SUCCESSFUL USDT DEPOSITS
  ========================================================= */
  
  const getWednesdaySuccessfulDeposits =
    async (
      username
    ) => {
  
      /* =====================================================
         USDT COLLECTION QUERY
      ===================================================== */
  
      const usdtQuery =
        query(
  
          collection(
            db,
            USDT_COLLECTION
          ),
  
          where(
            "username",
            "==",
            username
          ),
  
          where(
            "status",
            "==",
            "success"
          )
  
        );
  
  
      /* =====================================================
         VISITOR COLLECTION QUERY
      ===================================================== */
  
      const visitorQuery =
        query(
  
          collection(
            db,
            VISITOR_COLLECTION
          ),
  
          where(
            "username",
            "==",
            username
          ),
  
          where(
            "status",
            "==",
            "success"
          )
  
        );
  
  
      /* =====================================================
         FETCH BOTH
      ===================================================== */
  
      const [
        usdtSnapshot,
        visitorSnapshot,
      ] = await Promise.all([
  
        getDocs(
          usdtQuery
        ),
  
        getDocs(
          visitorQuery
        ),
  
      ]);
  
  
      /* =====================================================
         NORMALIZE
      ===================================================== */
  
      const usdtDeposits =
        usdtSnapshot.docs.map(
          normalizeUsdtDeposit
        );
  
  
      const visitorDeposits =
        visitorSnapshot.docs.map(
          normalizeVisitorDeposit
        );
  
  
      /* =====================================================
         COMBINE
      ===================================================== */
  
      const allDeposits = [
  
        ...usdtDeposits,
  
        ...visitorDeposits,
  
      ];
  
  
      /* =====================================================
         FILTER ELIGIBLE DEPOSITS
      ===================================================== */
  
      const validDeposits =
        allDeposits.filter(
          item => {
  
            const timestamp =
              getTimestampSeconds(
                item
              );
  
  
            const depositType =
              String(
                item.depositOrderType ||
                item.position ||
                "other"
              )
                .trim()
                .toLowerCase();
  
  
            return (
  
              normalizeStatus(
                item.status
              ) ===
              "success" &&
  
              normalizeUsername(
                item.username
              ) ===
              normalizeUsername(
                username
              ) &&
  
              isWednesday(
                timestamp
              ) &&
  
              depositType ===
              "other" &&
  
              isUSDTTransaction(
                item
              ) &&
  
              Number(
                item.originalAmount ||
                0
              ) > 0
  
            );
  
          }
        );
  
  
      /* =====================================================
         OLD → NEW
      ===================================================== */
  
      validDeposits.sort(
        (
          a,
          b
        ) => {
  
          const timeA =
            getTimestampSeconds(
              a
            );
  
  
          const timeB =
            getTimestampSeconds(
              b
            );
  
  
          if (
            timeA ===
            timeB
          ) {
  
            return String(
              a.id
            ).localeCompare(
              String(
                b.id
              )
            );
  
          }
  
  
          return (
            timeA -
            timeB
          );
  
        }
      );
  
  
      return validDeposits;
  
    };
  
  
  /* =========================================================
     COMPONENT
  ========================================================= */
  
  export default function WeeklyDepositBonusUSDT() {
  
    const [
      username,
      setUsername,
    ] = useState("");
  
  
    const [
      deposit,
      setDeposit,
    ] = useState(null);
  
  
    const [
      bonus,
      setBonus,
    ] = useState(0);
  
  
    const [
      bonusCurrency,
      setBonusCurrency,
    ] = useState(
      USDT_CURRENCY
    );
  
  
    const [
      loading,
      setLoading,
    ] = useState(true);
  
  
    const [
      applying,
      setApplying,
    ] = useState(false);
  
  
    const [
      completed,
      setCompleted,
    ] = useState(false);
  
  
    const [
      message,
      setMessage,
    ] = useState("");
  
  
    /* =======================================================
       BACK
    ======================================================= */
  
    const handleBack = () => {
  
      if (
        window.history.length > 1
      ) {
  
        window.history.back();
  
        return;
  
      }
  
  
      window.location.href =
        "/";
  
    };
  
  
    /* =======================================================
       LOAD WEEKLY BONUS
    ======================================================= */
  
    useEffect(
      () => {
  
        let mounted = true;
  
  
        const loadWeeklyBonus =
          async () => {
  
            try {
  
              if (mounted) {
  
                setLoading(true);
  
                setMessage("");
  
              }
  
  
              /* =================================================
                 USERNAME
              ================================================= */
  
              const storedUsername =
                localStorage.getItem(
                  "username"
                );
  
  
              if (
                !storedUsername
              ) {
  
                if (mounted) {
  
                  setMessage(
                    "Login required."
                  );
  
                }
  
                return;
  
              }
  
  
              if (mounted) {
  
                setUsername(
                  storedUsername
                );
  
              }
  
  
              /* =================================================
                 FIND USER
              ================================================= */
  
              const userDoc =
                await findUserByUsername(
                  storedUsername
                );
  
  
              if (
                !userDoc
              ) {
  
                if (mounted) {
  
                  setMessage(
                    "Account not found."
                  );
  
                }
  
                return;
  
              }
  
  
              const userData =
                userDoc.data();
  
  
              /* =================================================
                 GET DEPOSITS
              ================================================= */
  
              const deposits =
                await getWednesdaySuccessfulDeposits(
                  storedUsername
                );
  
  
              if (
                deposits.length === 0
              ) {
  
                if (mounted) {
  
                  setMessage(
                    "Hakuna USDT deposit ya Jumatano inayostahili Weekly Bonus."
                  );
  
                }
  
                return;
  
              }
  
  
              /* =================================================
                 CLAIMED IDS
              ================================================= */
  
              const claimedIds =
                Array.isArray(
                  userData.weeklyBonusClaimedTxIds
                )
                  ? userData.weeklyBonusClaimedTxIds
                  : [];
  
  
              /* =================================================
                 FIND UNCLAIMED
              ================================================= */
  
              const availableDeposit =
                deposits.find(
                  tx => {
  
                    const uniqueKey =
                      `${tx.source}_${tx.id}`;
  
  
                    return (
  
                      !claimedIds.includes(
                        uniqueKey
                      ) &&
  
                      tx.selfBonusGiven !==
                      true
  
                    );
  
                  }
                );
  
  
              /* =================================================
                 NOTHING AVAILABLE
              ================================================= */
  
              if (
                !availableDeposit
              ) {
  
                if (mounted) {
  
                  setCompleted(true);
  
                  setMessage(
                    "Umeshapokea Weekly USDT Deposit Bonus kwa deposit zako za Jumatano."
                  );
  
                }
  
                return;
  
              }
  
  
              /* =================================================
                 AMOUNT
              ================================================= */
  
              const originalAmount =
                Number(
                  availableDeposit.originalAmount ||
                  availableDeposit.amount ||
                  0
                );
  
  
              if (
                originalAmount <= 0
              ) {
  
                if (mounted) {
  
                  setMessage(
                    "USDT deposit amount si sahihi."
                  );
  
                }
  
                return;
  
              }
  
  
              /* =================================================
                 CURRENCY
              ================================================= */
  
              const currency =
                getTransactionCurrency(
                  availableDeposit
                );
  
  
              if (
                currency !==
                USDT_CURRENCY
              ) {
  
                if (mounted) {
  
                  setMessage(
                    "Deposit hii si ya USDT."
                  );
  
                }
  
                return;
  
              }
  
  
              /* =================================================
                 BONUS
              ================================================= */
  
              const calculatedBonus =
                Math.round(
                  (
                    originalAmount *
                    WEEKLY_PERCENT
                  ) /
                  100 *
                  100000000
                ) /
                100000000;
  
  
              if (
                calculatedBonus <= 0
              ) {
  
                if (mounted) {
  
                  setMessage(
                    "Bonus amount si sahihi."
                  );
  
                }
  
                return;
  
              }
  
  
              /* =================================================
                 SET STATE
              ================================================= */
  
              if (mounted) {
  
                setDeposit(
                  availableDeposit
                );
  
                setBonus(
                  calculatedBonus
                );
  
                setBonusCurrency(
                  USDT_CURRENCY
                );
  
                setMessage("");
  
              }
  
            }
  
            catch (error) {
  
              console.error(
                "Weekly USDT bonus loading error:",
                error
              );
  
  
              if (mounted) {
  
                setMessage(
                  error?.message ||
                  "Unable to load Weekly USDT Deposit Bonus."
                );
  
              }
  
            }
  
            finally {
  
              if (mounted) {
  
                setLoading(false);
  
              }
  
            }
  
          };
  
  
        loadWeeklyBonus();
  
  
        return () => {
  
          mounted = false;
  
        };
  
      },
      []
    );
  
  
    /* =======================================================
       APPLY BONUS
    ======================================================= */
  
    const applyBonus =
      async () => {
  
        if (
          !deposit ||
          !username ||
          !bonus ||
          applying ||
          completed
        ) {
  
          return;
  
        }
  
  
        setApplying(true);
  
        setMessage("");
  
  
        try {
  
          /* =================================================
             FIND USER
          ================================================= */
  
          const userDoc =
            await findUserByUsername(
              username
            );
  
  
          if (
            !userDoc
          ) {
  
            throw new Error(
              "User account not found."
            );
  
          }
  
  
          /* =================================================
             DEPOSIT REF
          ================================================= */
  
          const depositRef =
            doc(
              db,
              deposit.source,
              deposit.id
            );
  
  
          /* =================================================
             FINAL VALUES
          ================================================= */
  
          let finalBonus = 0;
  
          let finalAmount = 0;
  
          let finalWeekKey = "";
  
          let finalCurrency =
            USDT_CURRENCY;
  
  
          /* =================================================
             ATOMIC TRANSACTION
          ================================================= */
  
          await runTransaction(
            db,
            async (
              transaction
            ) => {
  
              /* =================================================
                 READ DEPOSIT
              ================================================= */
  
              const depositSnapshot =
                await transaction.get(
                  depositRef
                );
  
  
              if (
                !depositSnapshot.exists()
              ) {
  
                throw new Error(
                  "USDT Deposit haijapatikana."
                );
  
              }
  
  
              const freshDeposit =
                depositSnapshot.data();
  
  
              /* =================================================
                 STATUS
              ================================================= */
  
              if (
                normalizeStatus(
                  freshDeposit.status
                ) !==
                "success"
              ) {
  
                throw new Error(
                  "USDT Deposit si successful."
                );
  
              }
  
  
              /* =================================================
                 TIMESTAMP
              ================================================= */
  
              const depositTimestamp =
                getTimestampSeconds(
                  freshDeposit
                );
  
  
              if (
                depositTimestamp <= 0
              ) {
  
                throw new Error(
                  "Deposit timestamp haijapatikana."
                );
  
              }
  
  
              /* =================================================
                 WEDNESDAY
              ================================================= */
  
              if (
                !isWednesday(
                  depositTimestamp
                )
              ) {
  
                throw new Error(
                  "Deposit hii haikufanyika Jumatano."
                );
  
              }
  
  
              /* =================================================
                 DEPOSIT TYPE
              ================================================= */
  
              const depositType =
                String(
                  freshDeposit.depositOrderType ||
                  freshDeposit.position ||
                  "other"
                )
                  .trim()
                  .toLowerCase();
  
  
              if (
                depositType !==
                "other"
              ) {
  
                throw new Error(
                  "Deposit hii si eligible kwa Weekly Bonus."
                );
  
              }
  
  
              /* =================================================
                 CURRENCY
              ================================================= */
  
              const freshCurrency =
                getTransactionCurrency({
  
                  ...freshDeposit,
  
                  source:
                    deposit.source,
  
                });
  
  
              if (
                freshCurrency !==
                USDT_CURRENCY
              ) {
  
                throw new Error(
                  "Deposit hii si ya USDT."
                );
  
              }
  
  
              /* =================================================
                 READ USER
              ================================================= */
  
              const userSnapshot =
                await transaction.get(
                  userDoc.ref
                );
  
  
              if (
                !userSnapshot.exists()
              ) {
  
                throw new Error(
                  "User account not found."
                );
  
              }
  
  
              const userData =
                userSnapshot.data();
  
  
              /* =================================================
                 CLAIMED IDS
              ================================================= */
  
              const claimedIds =
                Array.isArray(
                  userData.weeklyBonusClaimedTxIds
                )
                  ? userData.weeklyBonusClaimedTxIds
                  : [];
  
  
              /* =================================================
                 UNIQUE TRANSACTION KEY
              ================================================= */
  
              const uniqueTxKey =
                `${deposit.source}_${deposit.id}`;
  
  
              /* =================================================
                 DOUBLE CLAIM
              ================================================= */
  
              if (
                claimedIds.includes(
                  uniqueTxKey
                )
              ) {
  
                throw new Error(
                  "Weekly Bonus hii tayari imetolewa."
                );
  
              }
  
  
              /* =================================================
                 SELF BONUS PROTECTION
              ================================================= */
  
              if (
                freshDeposit.selfBonusGiven ===
                true
              ) {
  
                throw new Error(
                  "Bonus tayari imetolewa kwenye deposit hii."
                );
  
              }
  
  
              /* =================================================
                 ADMIN BONUS PROTECTION
              ================================================= */
  
              if (
                freshDeposit.adminBonusGiven ===
                true
              ) {
  
                throw new Error(
                  "Bonus tayari imelipwa na Admin."
                );
  
              }
  
  
              /* =================================================
                 AMOUNT
              ================================================= */
  
              finalAmount =
                Number(
                  freshDeposit.amount ||
                  0
                );
  
  
              if (
                finalAmount <= 0
              ) {
  
                throw new Error(
                  "USDT Deposit amount haijapatikana."
                );
  
              }
  
  
              /* =================================================
                 CURRENCY
              ================================================= */
  
              finalCurrency =
                USDT_CURRENCY;
  
  
              /* =================================================
                 BALANCE FIELD
              ================================================= */
  
              /*
                VERY IMPORTANT:
  
                Firebase field:
                usdtBalance
  
                NOT:
                USDTBalance
              */
  
              const balanceField =
                USDT_BALANCE_FIELD;
  
  
              /* =================================================
                 CALCULATE BONUS
              ================================================= */
  
              finalBonus =
                Math.round(
                  (
                    finalAmount *
                    WEEKLY_PERCENT
                  ) /
                  100 *
                  100000000
                ) /
                100000000;
  
  
              if (
                finalBonus <= 0
              ) {
  
                throw new Error(
                  "Weekly USDT Bonus amount si sahihi."
                );
  
              }
  
  
              /* =================================================
                 WEEK KEY
              ================================================= */
  
              finalWeekKey =
                getWednesdayKey(
                  depositTimestamp
                );
  
  
              /* =================================================
                 CURRENT BALANCE
              ================================================= */
  
              const currentBalance =
                Number(
                  userData[
                    balanceField
                  ] || 0
                );
  
  
              const newBalance =
                currentBalance +
                finalBonus;
  
  
              /* =================================================
                 UPDATE USER
              ================================================= */
  
              transaction.update(
                userDoc.ref,
                {
  
                  /*
                    Correct Firebase field:
                    usdtBalance
                  */
  
                  [balanceField]:
                    newBalance,
  
  
                  weeklyBonusClaimedTxIds:
                    [
                      ...claimedIds,
                      uniqueTxKey,
                    ],
  
  
                  lastWeeklyBonusAt:
                    serverTimestamp(),
  
  
                  lastWeeklyBonusAmount:
                    finalBonus,
  
  
                  lastWeeklyBonusCurrency:
                    USDT_CURRENCY,
  
  
                  lastWeeklyBonusWeek:
                    finalWeekKey,
  
  
                  lastWeeklyBonusTransactionId:
                    deposit.id,
  
  
                  lastWeeklyBonusSource:
                    deposit.source,
  
                }
              );
  
  
              /* =================================================
                 UPDATE DEPOSIT
              ================================================= */
  
              transaction.update(
                depositRef,
                {
  
                  selfBonusGiven:
                    true,
  
  
                  adminBonusGiven:
                    false,
  
  
                  selfBonusAmount:
                    finalBonus,
  
  
                  selfBonusClaimedAmount:
                    finalBonus,
  
  
                  selfBonusPercent:
                    WEEKLY_PERCENT,
  
  
                  selfBonusLabel:
                    "Weekly USDT Deposit Bonus 5%",
  
  
                  selfBonusType:
                    "weekly",
  
  
                  selfBonusLocked:
                    false,
  
  
                  selfBonusApprovedBy:
                    "user",
  
  
                  selfBonusApprovedAt:
                    serverTimestamp(),
  
  
                  originalDepositAmount:
                    finalAmount,
  
  
                  sourceCurrency:
                    USDT_CURRENCY,
  
  
                  bonusCurrency:
                    USDT_CURRENCY,
  
  
                  /*
                    Correct field:
                    usdtBalance
                  */
  
                  bonusBalanceField:
                    USDT_BALANCE_FIELD,
  
  
                  bonusDepositAmount:
                    finalAmount,
  
  
                  bonusDepositCurrency:
                    USDT_CURRENCY,
  
  
                  weeklyBonusWeek:
                    finalWeekKey,
  
  
                  bonusStatus:
                    "credited",
  
  
                  bonusTransactionType:
                    "depositBonus",
  
  
                  depositOrderType:
                    "other",
  
  
                  position:
                    "other",
  
                }
              );
  
            }
          );
  
  
          /* =================================================
             SUCCESS
          ================================================= */
  
          setCompleted(true);
  
  
          setBonus(
            finalBonus
          );
  
  
          setBonusCurrency(
            finalCurrency
          );
  
  
          setMessage(
            `Hongera! ${formatMoney(
              finalBonus,
              finalCurrency
            )} Weekly Bonus imeongezwa kwenye usdtBalance yako.`
          );
  
        }
  
        catch (error) {
  
          console.error(
            "Weekly USDT bonus payment failed:",
            error
          );
  
  
          setMessage(
            error?.message ||
            "Weekly USDT Bonus payment failed."
          );
  
        }
  
        finally {
  
          setApplying(false);
  
        }
  
      };
  
  
    /* =======================================================
       LOADING
    ======================================================= */
  
    if (
      loading
    ) {
  
      return (
  
        <div className="wb-page">
  
          <div
            className="
              wb-card
              wb-loading
            "
          >
  
            <button
              type="button"
              className="wb-back-btn"
              onClick={handleBack}
              aria-label="Go back"
            >
  
              <span>
                ←
              </span>
  
              Back
  
            </button>
  
  
            <div
              className="
                wb-loader
              "
            />
  
  
            <p>
              Checking Weekly USDT Deposit Bonus...
            </p>
  
          </div>
  
        </div>
  
      );
  
    }
  
  
    /* =======================================================
       CURRENT CURRENCY
    ======================================================= */
  
    const currentCurrency =
      deposit
        ? USDT_CURRENCY
        : bonusCurrency;
  
  
    /* =======================================================
       RENDER
    ======================================================= */
  
    return (
  
      <div className="wb-page">
  
        <div className="wb-card">
  
  
          {/* =================================================
              BACK
          ================================================= */}
  
          <button
            type="button"
            className="wb-back-btn"
            onClick={handleBack}
            aria-label="Go back"
          >
  
            <span>
              ←
            </span>
  
            Back
  
          </button>
  
  
          {/* =================================================
              ICON
          ================================================= */}
  
          <div className="wb-gift">
            🎉
          </div>
  
  
          {/* =================================================
              BADGE
          ================================================= */}
  
          <div className="wb-badge">
  
            WEEKLY USDT BONUS
  
          </div>
  
  
          {/* =================================================
              TITLE
          ================================================= */}
  
          <h2>
  
            Wednesday USDT Deposit Bonus
  
          </h2>
  
  
          {/* =================================================
              SUBTITLE
          ================================================= */}
  
          <p
            className="
              wb-subtitle
            "
          >
  
            Hongera{" "}
  
            <strong>
  
              {username ||
                "Trader"}
  
            </strong>
  
            !
  
            <br />
  
            USDT deposit yako ya Jumatano
            inastahili Weekly Bonus.
  
            <br />
  
            Unaweza ku-claim bonus yako
            siku yoyote baada ya deposit
            ya Jumatano.
  
          </p>
  
  
          {/* =================================================
              DEPOSIT BOX
          ================================================= */}
  
          {deposit && (
  
            <div
              className="
                wb-deposit-box
              "
            >
  
              <div>
  
                <span>
                  USDT Deposit
                </span>
  
                <strong>
  
                  {formatMoney(
                    deposit.originalAmount ||
                    deposit.amount ||
                    0,
                    currentCurrency
                  )}
  
                </strong>
  
              </div>
  
  
              <div>
  
                <span>
                  Bonus Rate
                </span>
  
                <strong>
  
                  {WEEKLY_PERCENT}%
  
                </strong>
  
              </div>
  
  
              <div>
  
                <span>
                  Currency
                </span>
  
                <strong>
  
                  USDT
  
                </strong>
  
              </div>
  
  
              <div>
  
                <span>
                  Balance
                </span>
  
                <strong>
  
                  usdtBalance
  
                </strong>
  
              </div>
  
  
              <div>
  
                <span>
                  Source
                </span>
  
                <strong>
  
                  {
                    deposit.source ===
                    VISITOR_COLLECTION
  
                      ? "Visitor Deposit"
  
                      : "USDT Deposit"
                  }
  
                </strong>
  
              </div>
  
  
              {deposit.country && (
  
                <div>
  
                  <span>
                    Country
                  </span>
  
                  <strong>
  
                    {deposit.country}
  
                  </strong>
  
                </div>
  
              )}
  
            </div>
  
          )}
  
  
          {/* =================================================
              BONUS BOX
          ================================================= */}
  
          {bonus > 0 && (
  
            <div
              className="
                wb-bonus-box
              "
            >
  
              <span>
  
                YOUR WEEKLY USDT BONUS
  
              </span>
  
  
              <strong>
  
                {formatMoney(
                  bonus,
                  bonusCurrency
                )}
  
              </strong>
  
  
              <small>
  
                {WEEKLY_PERCENT}%
                Wednesday USDT Deposit Bonus
  
              </small>
  
            </div>
  
          )}
  
  
          {/* =================================================
              MESSAGE
          ================================================= */}
  
          {message && (
  
            <div
              className={`
                wb-message
                ${
                  completed
                    ? "wb-success"
                    : "wb-info"
                }
              `}
            >
  
              <span>
  
                {
                  completed
                    ? "✓"
                    : "!"
                }
  
              </span>
  
  
              <p>
  
                {message}
  
              </p>
  
            </div>
  
          )}
  
  
          {/* =================================================
              APPLY BUTTON
          ================================================= */}
  
          {bonus > 0 &&
          !completed && (
  
            <button
              type="button"
              className="
                wb-apply-btn
              "
              onClick={
                applyBonus
              }
              disabled={
                applying
              }
            >
  
              {applying ? (
  
                <>
  
                  <span
                    className="
                      wb-button-loader
                    "
                  />
  
                  Processing...
  
                </>
  
              ) : (
  
                <>
  
                  APPLY WEEKLY USDT BONUS
  
                  <span>
                    →
                  </span>
  
                </>
  
              )}
  
            </button>
  
          )}
  
  
          {/* =================================================
              PAID
          ================================================= */}
  
          {completed && (
  
            <div
              className="
                wb-paid
              "
            >
  
              <span>
                ✓
              </span>
  
              Weekly USDT Bonus Already Claimed
  
            </div>
  
          )}
  
  
          {/* =================================================
              NOTE
          ================================================= */}
  
          <p
            className="
              wb-note
            "
          >
  
            Weekly USDT Deposit Bonus ni{" "}
  
            <strong>
              {WEEKLY_PERCENT}%
            </strong>
  
            {" "}ya deposit ya USDT iliyofanyika Jumatano.
  
            <br />
  
            Unaweza ku-claim bonus yako
            siku yoyote baada ya Jumatano.
  
            <br />
  
            Bonus inaingia kwenye{" "}
  
            <strong>
              usdtBalance
            </strong>
  
            {" "}yako.
  
          </p>
  
  
        </div>
  
      </div>
  
    );
  
  }
